/**
 * YAML Validate Tool
 *
 * Validates YAML syntax and reports errors with position details.
 */

import { parse as parseYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const YamlValidateTool: ITransformerTool = {
  id: 'yaml-validate',
  name: 'YAML Validate',
  description: 'Validate YAML syntax and report any errors',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['yaml', 'validate', 'check', 'verify', 'lint', 'syntax'],

  seo: {
    slug: 'yaml-validator',
    pageTitle: 'YAML Validator - Free Online Syntax Checker | SWE DevTools',
    metaDescription: 'Validate YAML syntax and see detailed error messages with line numbers. Check structure and formatting instantly. Fast, private, runs in your browser.',
    keywords: ['yaml validator', 'yaml syntax checker', 'validate yaml online', 'yaml lint', 'yaml checker'],
    relatedTools: ['yaml-to-json', 'yaml-to-xml', 'json-validate'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.YAML, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'YAML' || detectedType === 'Text';
  },

  async transform(input: string): Promise<string> {
    try {
      const parsed = parseYaml(input);
      const type = Array.isArray(parsed)
        ? 'array'
        : parsed === null
          ? 'null'
          : typeof parsed;
      const detail =
        type === 'array'
          ? `Items: ${parsed.length}`
          : type === 'object'
            ? `Keys: ${Object.keys(parsed as Record<string, unknown>).length}`
            : '';

      return `✓ Valid YAML\nType: ${type}${detail ? '\n' + detail : ''}`;
    } catch (e) {
      const error = e as Error;
      return `✗ Invalid YAML\nError: ${error.message}`;
    }
  },
};
